import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  updateIdentityField,
  setIdentityType,
} from '../../Slice/unifiedFormSlice';
import * as S from './Styles';
import FileUpload from '../../helper/FileUpload';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeftOutlined';
import { Typography, Box } from '@mui/material';
import Text from '../Texts';
import { legalFields } from './fields';
import { useFormik } from 'formik';
import { validationSchema } from './validationLegal';
import { useNavigate, useLocation } from 'react-router-dom';
import uploadIcon from '/src/assets/images/identity/paper-upload.svg?url';
import { submitIdentityExtraInfo } from '../../thunks/submitIdentityExtraInfo';
import { toast } from 'react-toastify';
import { useSubmitHandler } from '../../hooks/useSubmitHandler';
import SubmitButton from '../Button/SubmitButton';
import ContractInfoModal from './ContractInfoModal';
import NumberConverter from '../../helper/ConvertPersionNumberToEnglish';

interface LegalIdentityProps {
  onNext: () => void;
  identityType?: string;
}

const LegalIdentity: React.FC<LegalIdentityProps> = ({
  onNext,
  identityType,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const identity = useSelector(
    (state: RootState) => state.unifiedForm.identity,
  );
  const [selectedFiles, setSelectedFiles] = useState<{
    national_id_image: File | null;
    contract: File | null;
  }>({
    national_id_image: null,
    contract: null,
  });
  const [backendErrors, setBackendErrors] = useState<any>({});
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);

  // استفاده از hook برای مدیریت submit
  const { isSubmitting, handleSubmit } = useSubmitHandler({
    successMessage: 'اطلاعات هویت حقوقی با موفقیت ثبت شد',
    debounceMs: 2000, // 2 ثانیه بین submit ها
    onSuccess: () => {
      // تاخیر قبل از navigate
      setTimeout(() => {
        onNext();
      }, 800);
    },
  });

  const { id, name, display_name, identity_type } = location.state || {};

  console.log('LegalIdentity Location state:', {
    id,
    name,
    display_name,
    identity_type,
  });

  useEffect(() => {
    if (identity_type) {
      dispatch(setIdentityType(identity_type));
    }
    if (id) {
      dispatch(updateIdentityField({ field: 'id', value: id }));
    }
    // These were crossed over: `name` was written into display_name and
    // vice versa, so resuming a draft showed each value in the wrong field.
    if (name) {
      dispatch(updateIdentityField({ field: 'name', value: name }));
    }
    if (display_name) {
      dispatch(
        updateIdentityField({ field: 'display_name', value: display_name }),
      );
    }
  }, [id, name, display_name, identity_type, dispatch]);

  const formik = useFormik({
    initialValues: {
      name: identity.name || '',
      display_name: identity.display_name || '',
      national_id: identity.national_id || '',
      address: identity.address || '',
      postal_code: identity.postal_code || '',
      phone: identity.phone || '',
      national_id_image: null,
      contract: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        console.log('LegalIdentity Form submitted:', {
          values,
          selectedFiles,
          publisher_id: id || identity.id,
        });

        const response = await dispatch(
          submitIdentityExtraInfo({
            identity_type: identityType || 'LEGAL',
            national_id: values.national_id,
            address: values.address,
            postal_code: values.postal_code,
            phone: values.phone,
            publisher_id: String(id || identity.id || ''),
            national_id_image: selectedFiles.national_id_image,
            contract: selectedFiles.contract,
          }),
        ).unwrap();

        Object.entries(values).forEach(([key, value]) => {
          if (key !== 'national_id_image' && key !== 'contract') {
            dispatch(
              updateIdentityField({
                field: key as keyof typeof identity,
                value,
              }),
            );
          }
        });

        return response;
      });
    },
  });

  useEffect(() => {
    console.log('LegalIdentity Formik state:', {
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      isValid: formik.isValid,
    });
  }, [formik.values, formik.errors, formik.touched, formik.isValid]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    // Numeric fields are normalised here too, matching the edit forms: a code
    // typed in Persian digits or pasted with a space is otherwise reported as
    // the wrong length even though it reads as ten digits on screen.
    const isNumeric = legalFields.some(
      (field) => field.name === name && field.numeric,
    );
    const value = isNumeric
      ? NumberConverter.toNumericOnly(e.target.value)
      : e.target.value;

    formik.setFieldValue(name, value);
    dispatch(
      updateIdentityField({
        field: name as keyof typeof identity,
        value,
      }),
    );
    if (backendErrors[name]) {
      setBackendErrors({ ...backendErrors, [name]: '' });
    }
  };

  return (
    <S.Container>
      <S.StyledButton
        variant="contained"
        startIcon={<S.Icon />}
        onClick={() => navigate('/intro')}
      >
        {Text.legalIdentty.returnIdentity}
      </S.StyledButton>

      <S.Title variant="h6">{Text.legalIdentty.title}</S.Title>
      <S.Description variant="body1">
        {Text.legalIdentty.completedData}
      </S.Description>

      <form onSubmit={formik.handleSubmit}>
        <S.GridContainer>
          {legalFields.map((field) => (
            <S.FieldContainer key={field.name}>
              <S.Label>{field.label}</S.Label>
              <S.StyledTextField
                variant="outlined"
                placeholder={field.placeholder}
                size="small"
                name={field.name}
                value={
                  formik.values[field.name as keyof typeof formik.initialValues]
                }
                onChange={handleFieldChange}
                onBlur={formik.handleBlur}
                error={
                  (formik.touched[
                    field.name as keyof typeof formik.initialValues
                  ] &&
                    !!formik.errors[
                      field.name as keyof typeof formik.initialValues
                    ]) ||
                  !!backendErrors[field.name]
                }
                helperText={
                  (formik.touched[
                    field.name as keyof typeof formik.initialValues
                  ] &&
                    formik.errors[
                      field.name as keyof typeof formik.initialValues
                    ]) ||
                  backendErrors[field.name]
                }
              />
            </S.FieldContainer>
          ))}
        </S.GridContainer>

        <S.UploadContainer>
          <S.FieldContainer>
            <S.Label>{Text.legalIdentty.ImageIdentity}</S.Label>
            <FileUpload
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  setSelectedFiles((prev) => ({
                    ...prev,
                    national_id_image: files[0],
                  }));
                  formik.setFieldValue('national_id_image', files[0]);
                  console.log(
                    'LegalIdentity national_id_image selected:',
                    files[0].name,
                  );
                }
              }}
              imageSrc={uploadIcon}
              uploadText={Text.legalIdentty.uploadImage}
            />
            {formik.touched.national_id_image &&
              formik.errors.national_id_image && (
                <Typography color="error">
                  {formik.errors.national_id_image}
                </Typography>
              )}
          </S.FieldContainer>

          <S.FieldContainer>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}
            >
              <S.Label>تصویر معرفی نامه شرکت با سربرگ رسمی</S.Label>
              <Typography
                component="span"
                sx={{
                  color: '#1E88E5',
                  fontSize: '12px',
                  fontFamily: 'IRANYekanXFaNum',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  marginTop: '2px',
                  '&:hover': {
                    color: '#1565C0',
                  },
                }}
                onClick={() => setIsContractModalOpen(true)}
              >
                توضیحات
              </Typography>
            </Box>
            <FileUpload
              onFilesSelected={(files) => {
                if (files.length > 0) {
                  setSelectedFiles((prev) => ({
                    ...prev,
                    contract: files[0],
                  }));
                  formik.setFieldValue('contract', files[0]);
                  console.log(
                    'LegalIdentity contract selected:',
                    files[0].name,
                  );
                }
              }}
              imageSrc={uploadIcon}
              uploadText="آپلود تصویر معرفی نامه شرکت با سربرگ رسمی"
            />
            {formik.touched.contract && formik.errors.contract && (
              <Typography color="error">{formik.errors.contract}</Typography>
            )}
          </S.FieldContainer>
        </S.UploadContainer>

        {backendErrors.general && (
          <S.FieldContainer>
            <Typography color="error">{backendErrors.general}</Typography>
          </S.FieldContainer>
        )}

        <S.SubmitButtonBox>
          <SubmitButton
            type="submit"
            startIcon={<ArrowCircleLeft />}
            isSubmitting={isSubmitting}
            loadingText="در حال ارسال..."
            variant="contained"
            color="primary"
            fullWidth
          >
            {Text.legalIdentty.submite}
          </SubmitButton>
        </S.SubmitButtonBox>
      </form>

      <ContractInfoModal
        open={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
      />
    </S.Container>
  );
};

export default LegalIdentity;
